import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Settings, Save, CloudOff, Calendar } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SetupPage = ({ user, settings: initialSettings, setSettings: updateSettings }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(initialSettings);
  const [formData, setFormData] = useState({
    tax_year: initialSettings?.tax_year || 2025,
    google_drive_backup: initialSettings?.google_drive_backup || false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let response;
      if (settings) {
        // Update existing settings
        response = await axios.put(`${API}/settings`, formData, {
          withCredentials: true,
        });
        toast.success("Paramètres mis à jour avec succès");
      } else {
        // Create new settings
        response = await axios.post(`${API}/settings`, formData, {
          withCredentials: true,
        });
        toast.success("Configuration initiale terminée");
      }
      
      if (updateSettings) {
        updateSettings(response.data);
      }
      navigate("/dashboard");
    } catch (error) {
      console.error("Settings error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-lg shadow-lg" data-testid="setup-card">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto h-14 w-14 rounded-xl bg-primary flex items-center justify-center mb-4">
            <Settings className="h-7 w-7 text-primary-foreground" />
          </div>
          <CardTitle className="text-2xl font-bold tracking-tight">
            {settings ? "Paramètres" : "Configuration Initiale"}
          </CardTitle>
          <CardDescription>
            {settings
              ? "Modifiez les paramètres de l'application"
              : "Configurez votre application de suivi des déclarations"}
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Tax Year */}
            <div className="space-y-2">
              <Label htmlFor="tax_year" className="text-sm font-medium flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                Année d'imposition
              </Label>
              <Input
                id="tax_year"
                type="number"
                min="2020"
                max="2030"
                value={formData.tax_year}
                onChange={(e) =>
                  setFormData({ ...formData, tax_year: parseInt(e.target.value) })
                }
                className="font-mono"
                data-testid="tax-year-input"
              />
              <p className="text-xs text-muted-foreground">
                Les codes clients seront générés avec cette année (ex: IM2025001)
              </p>
            </div>

            {/* Google Drive Backup */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border">
              <div className="flex items-center gap-3">
                <CloudOff className="h-5 w-5 text-muted-foreground" />
                <div>
                  <Label htmlFor="backup" className="text-sm font-medium cursor-pointer">
                    Sauvegarde Google Drive
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Fonctionnalité simulée pour cette version
                  </p>
                </div>
              </div>
              <Switch
                id="backup"
                checked={formData.google_drive_backup}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, google_drive_backup: checked })
                }
                data-testid="backup-switch"
              />
            </div>

            {/* Admin Info */}
            {!settings && user && (
              <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                <p className="text-sm text-foreground">
                  <span className="font-medium">Administrateur:</span> {user.name || user.email}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Vous serez défini comme administrateur de l'application
                </p>
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3 pt-4">
              {settings && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/dashboard")}
                  className="flex-1"
                  data-testid="cancel-button"
                >
                  Annuler
                </Button>
              )}
              <Button
                type="submit"
                className="flex-1"
                disabled={loading}
                data-testid="save-settings-button"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {settings ? "Enregistrer" : "Démarrer"}
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SetupPage;
